import { isAlive, isReadOnlyNode, isStateTreeNode, type SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { defaults, find, omit } from "lodash";
import type { ValidateOption as ValidateOptions } from "validate.js";
import validate from "validate.js";
import type { Thunk } from "../utils";
import { unthunk } from "../utils";
import type { WorkspacePageType } from "../WorkspacePageType";
import "./CommonValidators";
import type { ConstraintsIn, ValidatorOptionsOut } from "./Constraints";
import "./ModelFieldConfigurationProblems";
import { numericSeverity, ProblemSeverity } from "./ProblemSeverity";
import type { ProblemMessage } from "./ProblemsList";
import { EmptyReadOnlyProblemList, ProblemsList } from "./ProblemsList";

export type ProblemFinder<T extends Record<string, any>> = (node: T) => ProblemsList;

export type DetailedErrorMessage = { message: string; inContextMessage?: string };

/**
 * Derived from "detailed" example at http://validatejs.org/#validate-error-formatting
 */
type DetailedError<T extends Record<string, any>> = {
  [Validator in keyof ValidatorOptionsOut<T>]-?: {
    attribute: string;
    value: any;
    validator: Validator;
    globalOptions: ValidateOptions;
    attributes: T;
    options: NonNullable<ValidatorOptionsOut<T>[Validator]>;
    error: string | DetailedErrorMessage;
  };
}[keyof ValidatorOptionsOut<T>];

/**
 * Create a new validation function to validate a model with the given constraints
 * @param constraints An object used to define constraints that the node will be validated against
 * @param options
 * @returns
 */
export const createProblemFinder = <T extends Record<string, any>>(
  constraints: ConstraintsIn<T>,
  options: {
    /** The tab the problem occurred in */
    tabType: Thunk<WorkspacePageType, [T]>;

    /** Name of the node that this problem finder is attached to */
    name: Thunk<string, [T]>;

    sourceLocation?: Thunk<SnapshotIn<typeof ProblemMessage>["sourceLocation"] | undefined, [T]>;

    /*
     * By default, the name of the field being validated is attached to the problem message that is returned after validating.
     * Passing false will not append the node's name to the problem message, which is sometimes desirable.
     *
     * Alternatively, messages can be prefixed with ^ to disable full messages on a per-message basis.
     */
    fullMessages?: boolean;
  }
): ProblemFinder<T> => {
  const { tabType, name, fullMessages, sourceLocation } = defaults(options, {
    fullMessages: true,
  });

  return (node: T): ProblemsList => {
    if (isStateTreeNode(node) && !isReadOnlyNode(node) && !isAlive(node)) {
      return EmptyReadOnlyProblemList;
    }

    const rawErrors: DetailedError<T>[] | undefined = validate(node, constraints, { fullMessages, format: "detailed" });
    if (!rawErrors) {
      return EmptyReadOnlyProblemList;
    }

    const tagName = node.validation?.spec.tagName || node.validation?.spec.name;
    const input: SnapshotIn<typeof ProblemsList> = {
      groups: {},
    };

    for (const rawError of rawErrors) {
      // Get in-context message
      const rawICM = typeof rawError.error === "string" ? null : rawError.error.inContextMessage;
      const inContextMessage: string | undefined =
        rawICM ?? unthunk(rawError.options.inContextMessage, rawError.value, rawError.attributes);

      let problem = input.groups?.[rawError.attribute];
      if (!problem) {
        problem = input.groups![rawError.attribute] = {
          property: rawError.attribute,
          subNodeKey: rawError.options.subNodeKey ?? rawError.attribute,
          messages: [],
          tagName,
          nodeLabels: node.nodeLabels,
        };
      }

      const message = typeof rawError.error === "string" ? rawError.error : rawError.error.message;

      if (!message) {
        continue;
      }

      const messageData: SnapshotIn<typeof ProblemMessage> = {
        name: unthunk(name, node), // name of the node that this problem finder is attached to  - e.g. the model name "Test". These are thunkable because the problemFinder is attached once to the node, but most node's have user definable names and might open tabs to different places. This allows us to pull their values when the problems are being computed.
        tabType: unthunk(tabType, node), // the tab type to open in the Gadget editor if this problem links somewhere
        message: message.replace(/^\^/, ""),
        inContextMessage: inContextMessage ? inContextMessage.replace(/^\^/, "") : inContextMessage,
        severity: rawError.options ? rawError.options.severity : ProblemSeverity.Error,
        sourceLocation: sourceLocation ? unthunk(sourceLocation, node) : undefined,
      };

      // validate.js doesn't dedupe messages with different severities, so we manually dedupe & take the highest severity
      const duplicateMessage = find<SnapshotIn<typeof ProblemMessage>>(problem.messages, omit(messageData, "severity"));
      if (!duplicateMessage) {
        problem.messages!.push(messageData);
      } else if (numericSeverity(messageData.severity!) > numericSeverity(duplicateMessage.severity!)) {
        duplicateMessage.severity = messageData.severity;
      }
    }

    return ProblemsList.createReadOnly(input);
  };
};
